<!-- Preview Mode -->
<div *ngIf="mode === 'preview'" [ngStyle]="getPreviewStyles()" class="prose max-w-none p-6 rounded-lg">
  <div *ngIf="getHeading()" class="mb-6">
    <h2 [style.color]="getHeadingColor()" [ngClass]="getTitlePositionClass()" class="text-3xl font-bold">{{ getHeading() }}</h2>
  </div>

  <!-- No Images State -->
  <div *ngIf="!hasImages()" class="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
    <h3 class="mt-2 text-sm font-medium text-gray-900">Geen afbeeldingen</h3>
    <p class="mt-1 text-sm text-gray-500">Voeg afbeeldingen toe in de sidebar om een galerij te tonen.</p>
  </div>

  <!-- Grid Layout -->
  <div *ngIf="hasImages() && getLayoutType() === 'grid'"
       class="grid"
       [ngClass]="[getGridColumnsClass(), getGapClass()]">
    <div *ngFor="let image of getImages(); let i = index"
         class="relative overflow-hidden transition-all duration-300 cursor-pointer group"
         [ngClass]="[getShadowClass(), getHoverEffectClass()]"
         [style.border-radius.px]="getImageBorderRadius()"
         (click)="openLightbox(i)">

      <!-- Image -->
      <div [ngClass]="getAspectRatioClass()" class="overflow-hidden">
        <img [src]="image.url"
             [alt]="image.alt || ''"
             class="w-full h-full transition-transform duration-300"
             [ngStyle]="getImageStyles()"
             [attr.loading]="getLazyLoad() ? 'lazy' : 'eager'">
      </div>

      <!-- Caption Overlay (hover) -->
      <div *ngIf="getShowCaptions() && image.caption && getCaptionPosition() === 'hover'"
           class="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
        <p class="text-white text-sm">{{ image.caption }}</p>
      </div>

      <!-- Caption Overlay (always visible) -->
      <div *ngIf="getShowCaptions() && image.caption && getCaptionPosition() === 'overlay'"
           class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
        <p class="text-white text-sm">{{ image.caption }}</p>
      </div>
    </div>

    <!-- Captions Below (if that option is selected) -->
    <ng-container *ngIf="getShowCaptions() && getCaptionPosition() === 'below'">
      <div *ngFor="let image of getImages()" class="text-center">
        <p *ngIf="image.caption" [style.color]="getTextColor()" class="text-sm mt-2">{{ image.caption }}</p>
      </div>
    </ng-container>
  </div>

  <!-- Masonry Layout -->
  <div *ngIf="hasImages() && getLayoutType() === 'masonry'"
       class="columns-1 md:columns-2 lg:columns-3"
       [style.column-gap.px]="getGap()">
    <div *ngFor="let image of getImages(); let i = index"
         class="break-inside-avoid mb-4 overflow-hidden transition-all duration-300 cursor-pointer group"
         [ngClass]="[getShadowClass(), getHoverEffectClass()]"
         [style.border-radius.px]="getImageBorderRadius()"
         (click)="openLightbox(i)">

      <img [src]="image.url"
           [alt]="image.alt || ''"
           class="w-full h-auto transition-transform duration-300"
           [style.border-radius.px]="getImageBorderRadius()"
           [attr.loading]="getLazyLoad() ? 'lazy' : 'eager'">

      <!-- Caption -->
      <div *ngIf="getShowCaptions() && image.caption && getCaptionPosition() === 'below'"
           class="p-2">
        <p [style.color]="getTextColor()" class="text-sm">{{ image.caption }}</p>
      </div>
    </div>
  </div>

  <!-- Carousel/Slider Layout -->
  <div *ngIf="hasImages() && (getLayoutType() === 'carousel' || getLayoutType() === 'slider')"
       [class]="getFullWidthClass()"
       class="relative overflow-hidden">

    <!-- Main Slide -->
    <div class="relative">
      <img *ngIf="getCurrentCarouselImage()"
           [src]="getCurrentCarouselImage()!.url"
           [alt]="getCurrentCarouselImage()!.alt || ''"
           class="w-full h-auto"
           [ngStyle]="getImageStyles()">

      <!-- Content Overlay (Title, Description, CTA) -->
      <div *ngIf="getCarouselTitle() || getCarouselDescription() || getCarouselCTAs().length > 0"
           class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/60 to-transparent p-6">
        <div class="max-w-4xl mx-auto">
          <h3 *ngIf="getCarouselTitle()" class="text-2xl md:text-3xl font-bold text-white mb-2">
            {{ getCarouselTitle() }}
          </h3>
          <p *ngIf="getCarouselDescription()" class="text-white text-sm md:text-base mb-4 opacity-90">
            {{ getCarouselDescription() }}
          </p>
          <div *ngIf="getCarouselCTAs().length > 0" class="flex flex-wrap gap-3">
            <span *ngFor="let cta of getCarouselCTAs()"
               [class]="getCTAClass(cta.style || 'primary')"
               class="inline-flex items-center px-4 py-2 rounded-md font-medium transition-colors pointer-events-none">
              {{ cta.text }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation Arrows -->
    <button *ngIf="getShowNavigation()"
            (click)="prevSlide(); $event.stopPropagation()"
            class="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors">
      <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
      </svg>
    </button>

    <button *ngIf="getShowNavigation()"
            (click)="nextSlide(); $event.stopPropagation()"
            class="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors">
      <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
      </svg>
    </button>

    <!-- Dots Navigation -->
    <div *ngIf="getShowDots()" class="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
      <button *ngFor="let image of getImages(); let i = index"
              (click)="goToSlide(i); $event.stopPropagation()"
              class="w-3 h-3 rounded-full transition-colors"
              [ngClass]="i === carouselIndex ? 'bg-white' : 'bg-white/50 hover:bg-white/75'">
      </button>
    </div>
  </div>

  <!-- Hero Layout (single full-width image) -->
  <div *ngIf="hasImages() && getLayoutType() === 'hero'"
       class="relative overflow-hidden"
       [ngClass]="[getShadowClass()]"
       [style.border-radius.px]="getImageBorderRadius()">
    <div class="relative w-full" [style.height.px]="getImageHeight()">
      <img [src]="getImages()[0].url"
           [alt]="getImages()[0].alt || ''"
           class="w-full h-full"
           [ngStyle]="getImageStyles()">

      <!-- Overlay content (title, description) -->
      <div *ngIf="getImages()[0].title || getImages()[0].description"
           class="absolute inset-0 flex items-end bg-gradient-to-t from-black/60 to-transparent">
        <div class="p-6 w-full">
          <h3 *ngIf="getImages()[0].title" class="text-white text-2xl font-bold mb-1">
            {{ getImages()[0].title }}
          </h3>
          <p *ngIf="getImages()[0].description" class="text-white/90 text-sm">
            {{ getImages()[0].description }}
          </p>
        </div>
      </div>

      <!-- Caption overlay -->
      <div *ngIf="getShowCaptions() && getImages()[0].caption && getCaptionPosition() === 'overlay' && !getImages()[0].title"
           class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
        <p class="text-white text-sm">{{ getImages()[0].caption }}</p>
      </div>
    </div>

    <!-- Below caption -->
    <p *ngIf="getShowCaptions() && getImages()[0].caption && getCaptionPosition() === 'below'"
       [style.color]="getTextColor()" class="text-sm mt-2 px-1">
      {{ getImages()[0].caption }}
    </p>
  </div>

  <!-- Logo Ticker Layout (scrolling row) -->
  <div *ngIf="hasImages() && getLayoutType() === 'logo-ticker'"
       class="overflow-hidden relative"
       style="mask-image: linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%); -webkit-mask-image: linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%);">
    <div class="flex items-center ticker-scroll-container"
         [style.gap.px]="getGap()"
         [style.animation-duration.s]="getTickerDuration()">
      <ng-container *ngFor="let copy of [0, 1]">
        <div *ngFor="let image of getImages(); let i = index"
             class="flex-shrink-0 flex items-center justify-center"
             [style.width.px]="getTickerImageWidth()"
             [style.height.px]="getTickerImageWidth() * 0.6"
             [style.padding.px]="getTickerPadding()"
             [style.border-radius.px]="getImageBorderRadius()"
             style="background: rgba(255,255,255,0.04); border: 1px solid rgba(0,0,0,0.06);">
          <img [src]="image.url"
               [alt]="image.alt || ''"
               class="max-h-full max-w-full object-contain transition-all duration-300"
               style="filter: grayscale(100%)"
               (mouseenter)="$any($event.target).style.filter = 'grayscale(0%)'"
               (mouseleave)="$any($event.target).style.filter = 'grayscale(100%)'">
        </div>
      </ng-container>
    </div>
  </div>

  <!-- Justified Layout (all images same height, variable width) -->
  <div *ngIf="hasImages() && getLayoutType() === 'justified'"
       class="flex flex-wrap"
       [style.gap.px]="getGap()">
    <div *ngFor="let image of getImages(); let i = index"
         class="flex-grow overflow-hidden transition-all duration-300 cursor-pointer group"
         [ngClass]="[getShadowClass(), getHoverEffectClass()]"
         [style.border-radius.px]="getImageBorderRadius()"
         [style.height.px]="getImageHeight()"
         (click)="openLightbox(i)">

      <img [src]="image.url"
           [alt]="image.alt || ''"
           class="h-full w-auto max-w-none transition-transform duration-300"
           [style.border-radius.px]="getImageBorderRadius()"
           [style.object-fit]="getImageFit()"
           [attr.loading]="getLazyLoad() ? 'lazy' : 'eager'">
    </div>
  </div>
</div>

<!-- Lightbox Modal -->
<div *ngIf="lightboxOpen"
     class="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
     (click)="closeLightbox()">

  <!-- Close Button -->
  <button (click)="closeLightbox()"
          class="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10">
    <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
    </svg>
  </button>

  <!-- Previous Button -->
  <button (click)="prevImage(); $event.stopPropagation()"
          class="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10">
    <svg class="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
    </svg>
  </button>

  <!-- Image -->
  <div *ngIf="getCurrentLightboxImage()" class="max-w-[90vw] max-h-[90vh]" (click)="$event.stopPropagation()">
    <img [src]="getCurrentLightboxImage()!.url"
         [alt]="getCurrentLightboxImage()!.alt || ''"
         class="max-w-full max-h-[85vh] object-contain">

    <!-- Caption -->
    <p *ngIf="getCurrentLightboxImage()!.caption"
       class="text-white text-center mt-4 text-sm">
      {{ getCurrentLightboxImage()!.caption }}
    </p>
  </div>

  <!-- Next Button -->
  <button (click)="nextImage(); $event.stopPropagation()"
          class="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10">
    <svg class="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
    </svg>
  </button>

  <!-- Image Counter -->
  <div class="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm">
    {{ lightboxIndex + 1 }} / {{ getImages().length }}
  </div>
</div>

<!-- Edit Mode -->
<div *ngIf="mode === 'edit'">
  <p class="text-sm text-gray-400">Gebruik de sidebar om afbeeldingen en galerij opties aan te passen.</p>
</div>
